import { memo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { FieldArray, FieldInputProps, getIn } from "formik";
import { Box, IconButton, Typography } from "@mui/material";
import AppButton from "../../components/AppButton";
import { GridRowModel, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { FieldGroupFormDetails } from "../interfaces";
import { FormBuilder } from "../FormBuilder";
import { GenericObject } from "../../services/Price";
import { createInitialValues } from "../EditForm";

const buildNewEntry = (
  fieldsOptions: FieldGroupFormDetails,
  fieldGroupArray: GenericObject[]
): GenericObject => {
  const baseValues = createInitialValues(fieldsOptions.fields, null);

  if (
    !fieldsOptions.prefillFields ||
    fieldGroupArray.length === 0 ||
    !fieldGroupArray[0]
  ) {
    return baseValues;
  }

  const firstEntry = fieldGroupArray[0];
  const prefill: GenericObject = {};
  fieldsOptions.prefillFields.forEach((key) => {
    const formikKey = key.replace(/\./g, "__");
    if (firstEntry[formikKey] !== undefined) {
      prefill[formikKey] = firstEntry[formikKey];
    }
  });

  return { ...baseValues, ...prefill };
};

const FieldGroupForm = memo(
  ({
    dynamicPath,
    fieldsOptions,
    keyFieldPath,
    multipleSelection,
    isNestedForm,
    onRemoveGroup,
    row,
    rows,
  }: {
    dynamicPath?: boolean;
    fieldsOptions: FieldGroupFormDetails;
    keyFieldPath: string;
    multipleSelection?: GridRowSelectionModel;
    isNestedForm: boolean;
    onRemoveGroup: () => void;
    row?: GridRowModel;
    rows?: GridRowModel[];
  }) => {
    const intl = useIntl();

    return (
      <Box
        sx={(theme) => ({
          backgroundColor: theme.vars.palette.background.sunken,
          borderRadius: "6px",
          py: isNestedForm ? 0 : 1,
          my: 1,
        })}
      >
        {fieldsOptions.removeButtonDisplay === "button" && (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "end",
              marginBottom: 2,
            }}
          >
            <AppButton
              intent="tonal"
              aria-label="delete"
              startIcon={<DeleteIcon color="error" />}
              onClick={onRemoveGroup}
              sx={{ mx: 1 }}
            >
              {intl.formatMessage({ id: "DELETE" })}
            </AppButton>
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ flex: "1" }}>
            <FormBuilder
              keyFieldPath={keyFieldPath}
              fieldsOptions={fieldsOptions.fields}
              row={row}
              rows={rows}
              multipleSelection={multipleSelection}
              dynamicPath={dynamicPath}
              isNestedForm={true}
            />
          </Box>
          {fieldsOptions.removeButtonDisplay === "inline" && (
            <IconButton
              aria-label="close"
              sx={{
                color: "text.secondary",
                padding: 0,
                backgroundColor: "background.paper",
                borderRadius: "6px",
                height: "38px",
                width: "38px",
                mt: 1,
                mx: 1,
                mb: 0.5,
              }}
              title={intl.formatMessage({ id: "DELETE" })}
              onClick={onRemoveGroup}
            >
              <DeleteIcon sx={{ fontSize: "1.25rem" }} />
            </IconButton>
          )}
        </Box>
      </Box>
    );
  }
);

const FieldGroup = ({
  fieldsOptions,
  field,
  row,
  rows,
  multipleSelection,
  dynamicPath,
}: {
  fieldsOptions: FieldGroupFormDetails;
  field: FieldInputProps<any>;
  row?: GridRowModel;
  rows?: GridRowModel[];
  multipleSelection?: GridRowSelectionModel;
  dynamicPath?: boolean;
}) => {
  const isNested = field.name.includes(".");

  return (
    <FieldArray name={field.name}>
      {({ unshift, remove, form }) => {
        const fieldGroupArray: GenericObject[] =
          getIn(form.values, field.name) || [];

        return (
          <>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                mt: 1,
                mb: fieldGroupArray.length > 0 ? 1.5 : 0,
              }}
            >
              <Typography component={"p"}>
                <FormattedMessage id={fieldsOptions.label} />
              </Typography>
              <AppButton
                disabled={
                  fieldsOptions.limit
                    ? fieldGroupArray.length >= fieldsOptions.limit
                    : false
                }
                sx={{
                  color: "text.primary",
                  py: 0,
                  px: fieldsOptions.buttonLabel ? 1.25 : 0,
                  textTransform: "none",
                  minWidth: "32px",
                }}
                onClick={() =>
                  unshift(buildNewEntry(fieldsOptions, fieldGroupArray))
                }
              >
                {fieldsOptions.buttonLabel ? (
                  <FormattedMessage id={fieldsOptions.buttonLabel} />
                ) : (
                  <AddIcon fontSize="small" />
                )}
              </AppButton>
            </Box>
            {fieldGroupArray.map((_, index) => (
              <FieldGroupForm
                key={index}
                keyFieldPath={`${field.name}[${index}]`}
                fieldsOptions={fieldsOptions}
                row={row}
                rows={rows}
                multipleSelection={multipleSelection}
                dynamicPath={dynamicPath}
                onRemoveGroup={() => remove(index)}
                isNestedForm={isNested}
              />
            ))}
          </>
        );
      }}
    </FieldArray>
  );
};

export default FieldGroup;
